/**
 * Authentication service
 * Handles user signup, login, and token management
 */

import axios from "axios";
import api from "../api";
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "../constants";
import { tooManyTriesMessage } from "@/lib/utils/tooManyTries";

export interface SignupData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export interface LoginData {
  username: string;
  password: string;
}

export interface TokenResponse {
  access: string;
  refresh: string;
}

/**
 * Sign up a new user
 */
export async function signup(data: SignupData) {
  try {
    // Send only fields expected by backend serializer
    const payload = {
      username: data.username,
      email: data.email,
      password: data.password,
    };

    const response = await api.post("/api/users/signup/", payload);
    return response.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      throw new Error(err?.response?.data?.error || "Signup Failed");
    }
    throw new Error("An unexpected error occured!");
  }
}

/**
 * A refused login. `code` is the server's reason, e.g. "account_inactive" for
 * a correct password on an account that isn't active yet.
 */
export class LoginError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = "LoginError";
    this.code = code;
  }
}

/**
 * Log in an existing user
 * Uses cookie-based auth endpoint, with localStorage fallback
 */
export async function loginUser(data: LoginData) {
  try {
    // Drop any token left from an earlier session so the api interceptor
    // doesn't send it with this request; login issues a fresh one
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    delete api.defaults.headers.common["Authorization"];

    // Use new cookie-based login endpoint
    const response = await api.post("/api/users/login/", {
      username: data.username,
      password: data.password,
    });

    // Save access token in localStorage for Authorization header
    // Refresh token is stored in HttpOnly cookie by the server (not localStorage)
    if (response.data.access) {
      localStorage.setItem(ACCESS_TOKEN_KEY, response.data.access);
    }

    return response.data as TokenResponse;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data;
      if (error.response?.status === 429) throw new LoginError(tooManyTriesMessage(data?.detail));
      throw new LoginError(data?.message || data?.error || "Login failed", data?.code);
    }
    throw new Error("An unexpected error occurred!");
  }
}

/**
 * Log out the current user (clear cookies and localStorage)
 */
export async function logoutUser() {
  try {
    // Call server to clear HttpOnly cookies
    await api.post("/api/users/logout/");
  } catch {
    // Ignore errors - still clear localStorage
  }
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY); // clean up any legacy data
  delete api.defaults.headers.common["Authorization"];
}

/**
 * Decode a JWT token
 */
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired or will expire soon
 * @param token The JWT token to check
 * @param thresholdMs How many milliseconds before expiry to consider token as expired (default: 5 minutes)
 */
export function isTokenExpired(token: string, thresholdMs: number = 2 * 60 * 1000): boolean {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const timeUntilExpiry = expirationTime - currentTime;

  // Consider token expired if it's already expired or will expire within threshold
  return timeUntilExpiry <= thresholdMs;
}

/**
 * Refresh the access token using the refresh token (from cookie or localStorage)
 */
export async function refreshAccessToken(): Promise<string> {
  try {
    // _retry: true prevents the axios interceptor from recursively handling this call
    const response = await api.post("/api/users/token/refresh/", {}, { _retry: true } as any);

    const newAccessToken = response.data.access;
    if (newAccessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
      // Update axios default headers so subsequent requests use the new token
      api.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
    }

    return newAccessToken;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      // If refresh fails, clear tokens and throw
      await logoutUser();
      throw new Error(error.response?.data?.message || error.response?.data?.error || "Failed to refresh token");
    }
    throw new Error("An unexpected error occurred while refreshing token");
  }
}

/**
 * Get a valid access token, refreshing it if necessary
 */
export async function getValidAccessToken(): Promise<string> {
  let token = localStorage.getItem(ACCESS_TOKEN_KEY);

  if (!token) {
    throw new Error("No access token available. Please log in.");
  }

  // Check if token is expired or will expire soon
  if (isTokenExpired(token)) {
    console.log('[Auth] Access token expired or expiring soon, refreshing...');
    token = await refreshAccessToken();
  }

  return token;
}

/**
 * Change user password
 */
export async function changePassword(data: {
  current_password: string;
  new_password: string;
}) {
  try {
    const response = await api.post("/api/users/change-password/", data);
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw error; // Re-throw to preserve error details for the dialog
    }
    throw new Error("An unexpected error occurred while changing password");
  }
}
